"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DropdownButton = exports.DropdownButtonType = void 0;
var tslib_1 = require("tslib");
var tableau_ui_1 = require("@tableau/tableau-ui");
var React = require("react");
var TypeStyle = require("typestyle");
var Icons_1 = require("../../icons/Icons");
var Standard_1 = require("../../styles/Standard");
var DomUtil_1 = require("../../utils/DomUtil");
var FocusTrap_1 = require("../../utils/FocusTrap");
var GlobalPointerDownOutsideListener_1 = require("../../utils/GlobalPointerDownOutsideListener");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var StackingComponent_1 = require("../../utils/StackingComponent");
var DomWidgets_1 = require("../DomWidgets");
var DropdownButtonStyles = require("./DropdownButtonStyles");
var DropdownOverlay_1 = require("./DropdownOverlay");
var DropdownButtonType;
(function (DropdownButtonType) {
    DropdownButtonType[DropdownButtonType["Outline"] = 0] = "Outline";
    DropdownButtonType[DropdownButtonType["Line"] = 1] = "Line";
    DropdownButtonType[DropdownButtonType["Text"] = 2] = "Text";
    DropdownButtonType[DropdownButtonType["Icon"] = 3] = "Icon";
})(DropdownButtonType = exports.DropdownButtonType || (exports.DropdownButtonType = {}));
/** careful, these assume numeric values for DropdownButtonType enum */
var TypeNames = ['outline', 'line', 'text', 'icon'];
var DisplayFlex = TypeStyle.style({ display: 'flex' });
/**
 * A button which displays a popup when pressed.
 * The contents of the popup are provided by you!
 * If you're looking for the equivalent of an HTML `<select>`, use `SingleSelectDropdown`.
 */
var DropdownButton = /** @class */ (function (_super) {
    tslib_1.__extends(DropdownButton, _super);
    function DropdownButton() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = { isOpen: false };
        _this.unsubscribeFromGlobalMousedown = undefined;
        _this.dropdownButtonElement = null;
        _this.stackingRootRef = React.createRef();
        _this.dropdownOverlayRef = React.createRef();
        _this.defaultPopupId = DomUtil_1.generateId(); // prop was not previously required, so many run-time instances may not yet provide it
        _this.mouseDownOnButton = false; // see https://gitlab.tableausoftware.com/browser-clients/widgets/merge_requests/1406
        _this.labelId = DomUtil_1.generateId();
        _this.getLabelId = function () { return _this.props.labelId || _this.labelId; };
        _this.buttonId = DomUtil_1.generateId();
        _this.getButtonId = function () { return _this.props.id || _this.buttonId; };
        _this.handleGlobalMousedown = function (e) {
            var target = e.target;
            var popupId = _this.dropdownButtonElement.getAttribute('aria-controls');
            var overlayElem = document.getElementById(popupId);
            if (!overlayElem) {
                throw new Error("DropdownButton with textContent \"" + _this.dropdownButtonElement.textContent + "\" and testId \"" + _this.props.testId + "\": found no popup element with id matching aria-controls value \"" + popupId + "\"");
            }
            if (!(overlayElem.contains(target) || _this.dropdownButtonElement.contains(target))) {
                _this.mouseDownOnButton = false;
                _this.close();
            }
        };
        _this.handleClick = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.currentTarget.contains(e.target)) {
                _this.toggle();
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleMouseDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            _this.mouseDownOnButton = true;
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onMouseDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleMouseUp = ReactHelpers_1.mergeEventHandlers(function (e) {
            _this.mouseDownOnButton = false;
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onMouseUp) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleFocusChange = function (e) {
            if (e.target === e.currentTarget) {
                if (e.type === 'focus' && _this.mouseDownOnButton) {
                    // TFSID 1116354 don't flash a focus indicator if the dropdown is being clicked
                    e.currentTarget.blur();
                }
                else {
                    _this.setState({ focus: e.type === 'focus' });
                }
            }
        };
        _this.handleFocus = ReactHelpers_1.mergeEventHandlers(_this.handleFocusChange, function (e) { var _a, _b; return (_b = (_a = _this.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleBlur = ReactHelpers_1.mergeEventHandlers(_this.handleFocusChange, function (e) { var _a, _b; return (_b = (_a = _this.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleHover = function (e) { return _this.setState({ hover: !_this.props.disabled && e.type === 'mouseover' }); };
        _this.handleKeyDown = function (e) {
            var _a, _b;
            // swallow ESC events that plausibly originate from focus being returned to the button after the popup closed
            if (e.key === 'Escape' && e.repeat) {
                e.preventDefault();
                e.stopPropagation();
            }
            (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            if (e.defaultPrevented) {
                return;
            }
            if (e.key === 'ArrowDown' && !e.repeat) {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.click();
            }
        };
        _this.handlePortalKeyDown = function (e) {
            if (e.key === 'Escape' && _this.state.isOpen) {
                _this.close();
                e.preventDefault();
            }
            e.stopPropagation();
        };
        _this.handleButtonRef = function (ref) {
            _this.dropdownButtonElement = ref;
            ReactHelpers_1.assignRef(_this.props.buttonRef, ref);
        };
        _this.close = function () {
            _this.toggle(false);
        };
        _this.handleTrap = function (e) {
            // if the trap occurs because of a clickEvent on the DropdownButton, we let the ClickHandler trigger the close
            if (!_this.mouseDownOnButton) {
                _this.close();
            }
            _this.mouseDownOnButton = false;
        };
        _this.onOpened = function () {
            var _a, _b;
            // tslint:disable-next-line:deprecation
            if (_this.props.popupContent || _this.props.renderPopupContent) {
                _this.unsubscribeFromGlobalMousedown = GlobalPointerDownOutsideListener_1.GlobalPointerDownOutsideListener.subscribe(_this.dropdownButtonElement, _this.handleGlobalMousedown);
            }
            (_b = (_a = _this.props).onOpen) === null || _b === void 0 ? void 0 : _b.call(_a, function () { return _this.close(); }, _this.dropdownButtonElement.getBoundingClientRect());
        };
        _this.onClosed = function () {
            var _a, _b, _c;
            (_a = _this.unsubscribeFromGlobalMousedown) === null || _a === void 0 ? void 0 : _a.call(_this);
            _this.unsubscribeFromGlobalMousedown = undefined;
            if (_this.props.focusOnClose) {
                _this.dropdownButtonElement.focus();
            }
            (_c = (_b = _this.props).onClose) === null || _c === void 0 ? void 0 : _c.call(_b);
        };
        return _this;
    }
    DropdownButton.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (!prevProps.disabled && this.props.disabled) {
            if (this.state.isOpen) {
                this.close();
            }
            this.setState({ active: false, hover: false, focus: false });
        }
        if (!prevState.isOpen && this.state.isOpen) {
            this.onOpened();
        }
        else if (prevState.isOpen && !this.state.isOpen) {
            this.onClosed();
        }
    };
    DropdownButton.prototype.componentWillUnmount = function () {
        var _a;
        (_a = this.unsubscribeFromGlobalMousedown) === null || _a === void 0 ? void 0 : _a.call(this);
        this.unsubscribeFromGlobalMousedown = undefined;
    };
    /**
     * Toggles the dropdown popup.
     * @param open optional parameter, ```true``` to open the dropdown or ```false``` to close it.
     */
    DropdownButton.prototype.toggle = function (open) {
        if (open === void 0) { open = !this.state.isOpen; }
        if (this.props.disabled && open) {
            return;
        }
        if (open === this.state.isOpen) {
            return;
        }
        this.setState({ isOpen: open });
    };
    /**
     * Repositions the popup, if it is currently shown. Might be useful if the popup contents weren't finished sizing at the time that the
     * popup was shown, e.g. if it contained an image which was still loading.
     */
    DropdownButton.prototype.repositionPopup = function () {
        var _a;
        (_a = this.dropdownOverlayRef.current) === null || _a === void 0 ? void 0 : _a.reposition();
    };
    DropdownButton.prototype.render = function () {
        var _a;
        var _this = this;
        // tslint:disable-next-line:max-line-length deprecation
        var _b = this.props, buttonRef = _b.buttonRef, className = _b.className, closeOnPopupBlur = _b.closeOnPopupBlur, focusOnClose = _b.focusOnClose, label = _b.label, labelId = _b.labelId, message = _b.message, onClose = _b.onClose, onOpen = _b.onOpen, popupAlignment = _b.popupAlignment, popupContent = _b.popupContent, renderPopupContent = _b.renderPopupContent, stackingRootId = _b.stackingRootId, testId = _b.testId, type = _b.type, useDarkStyling = _b.useDarkStyling, valid = _b.valid, zIndex = _b.zIndex, htmlProps = tslib_1.__rest(_b, ["buttonRef", "className", "closeOnPopupBlur", "focusOnClose", "label", "labelId", "message", "onClose", "onOpen", "popupAlignment", "popupContent", "renderPopupContent", "stackingRootId", "testId", "type", "useDarkStyling", "valid", "zIndex"]);
        var labeledFieldProps = { disabled: htmlProps.disabled, id: this.getButtonId(), label: label, labelId: this.getLabelId(), message: message, valid: valid };
        return (React.createElement(tableau_ui_1.LabeledField, tslib_1.__assign({}, labeledFieldProps, { className: TypeStyle.classes(className, (_a = { dark: useDarkStyling }, _a[DisplayFlex] = type !== DropdownButtonType.Icon, _a)) }), function (fieldProps) {
            return React.createElement(StackingComponent_1.StackingRootContext.Consumer, null, function (stackingRoot) { return React.createElement(React.Fragment, null,
                !stackingRootId && !stackingRoot && React.createElement("div", { style: DropdownButtonStyles.PopupContainerStyle, ref: _this.stackingRootRef }),
                React.createElement("button", tslib_1.__assign({}, fieldProps, ReactHelpers_1.EventUtilsOptOut, { "aria-haspopup": true, "aria-labelledby": [_this.getLabelId(), _this.getButtonId()].join(' '), "data-tb-test-id": testId }, htmlProps, { "aria-atomic": true, "aria-controls": _this.state.isOpen ? (_this.props['aria-controls'] || _this.defaultPopupId) : undefined, "aria-expanded": !!_this.state.isOpen, "aria-live": _this.state.focus && !_this.state.isOpen ? 'assertive' : 'off', "aria-relevant": 'text', className: TypeStyle.classes(DropdownButtonStyles.ClassName, TypeNames[type]), onBlur: _this.handleBlur, onClick: _this.handleClick, onFocus: _this.handleFocus, onKeyDown: _this.handleKeyDown, onMouseOut: _this.handleHover, onMouseOver: _this.handleHover, onMouseDown: _this.handleMouseDown, onMouseUp: _this.handleMouseUp, ref: _this.handleButtonRef, tabIndex: htmlProps.disabled ? undefined : (htmlProps.tabIndex || 0) }),
                    _this.renderChildren(),
                    _this.renderIcon(),
                    _this.state.isOpen && (popupContent || renderPopupContent) &&
                        React.createElement(StackingComponent_1.StackingRootContext.Provider, { value: stackingRoot || _this.stackingRootRef.current },
                            React.createElement(DropdownPopup, { anchorEl: _this.dropdownButtonElement, children: popupContent || (renderPopupContent === null || renderPopupContent === void 0 ? void 0 : renderPopupContent()), onKeyDown: _this.handlePortalKeyDown, onTrap: closeOnPopupBlur ? _this.handleTrap : undefined, 
                                // TFS 961939 if `aria-controls was not provided, we add a default id for the popup;
                                // this does not fulfill the ARIA spec but prevents broken assumptions
                                overlayId: !_this.props['aria-controls'] ? _this.defaultPopupId : undefined, overlayRef: _this.dropdownOverlayRef, popupAlignment: popupAlignment, stackingRootId: stackingRootId, zIndex: zIndex })))); });
        }));
    };
    DropdownButton.prototype.renderChildren = function () {
        if (this.props.children === undefined || this.props.children === null) {
            return null;
        }
        return React.createElement("span", { className: DropdownButtonStyles.ChildrenClassName }, this.props.children);
    };
    DropdownButton.prototype.renderIcon = function () {
        var svgIcon = this.props.type === DropdownButtonType.Icon ? Icons_1.IconDropdownArrowIcon : Icons_1.DropdownArrowIcon;
        var src = svgIcon.icon({
            active: this.state.active || this.state.isOpen,
            disabled: this.props.disabled,
            hover: this.state.hover,
            onDarkBackground: this.props.useDarkStyling,
        });
        return React.createElement(DomWidgets_1.Img, { src: src, style: { margin: this.props.children ? undefined : "0 auto" } });
    };
    DropdownButton.defaultProps = {
        focusOnClose: true,
        popupAlignment: 'left',
        type: DropdownButtonType.Line,
        zIndex: 1,
    };
    return DropdownButton;
}(React.PureComponent));
exports.DropdownButton = DropdownButton;
var DropdownPopup = React.memo(function (props) {
    var _a;
    // hooks have to be called the same number of times in every render, so these need to be called before the potential early return
    var getAnchorClientRect = React.useCallback(function () { return props.anchorEl.getBoundingClientRect(); }, [props.anchorEl]);
    var portalEventTrapOverrides = React.useMemo(function () { return ({ onKeyDown: props.onKeyDown }); }, [props.onKeyDown]);
    return !props.children ? null :
        React.createElement(DropdownOverlay_1.DropdownOverlay, { alignment: props.popupAlignment, getAnchorClientRect: getAnchorClientRect, portalEventTrapOverrides: portalEventTrapOverrides, 
            // tslint:disable-next-line:deprecation
            stackingRootId: props.stackingRootId, zIndex: props.zIndex },
            React.createElement(FocusTrap_1.FocusTrap, { children: props.children, id: props.overlayId, style: tslib_1.__assign(tslib_1.__assign({}, Standard_1.PopupStyle), { minWidth: (_a = props.anchorEl) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width }), onTrap: props.onTrap }),
            props.onTrap &&
                // TFS 1080312 Add invisible div as "next thing to receive focus", allowing a document-final dropdown to close on TAB
                React.createElement("div", tslib_1.__assign({}, ReactHelpers_1.AxeOptOut, { "aria-hidden": true, tabIndex: 0 })));
});
DropdownPopup.displayName = 'DropdownPopup';
//# sourceMappingURL=DropdownButton.js.map